import { useMemo, useState, type ReactNode } from 'react';
import {
  BottomNavigation, BottomNavigationAction, Box, Divider, Drawer, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader,
  Paper, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  BusinessOutlined, GroupOutlined, LogoutOutlined, MoreHorizRounded, PersonOutlineOutlined, StoreMallDirectoryOutlined,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { useLogout } from '@/hooks/useAuthActions';
import { useRolePinGroups, withRolePins } from '@/hooks/useHomeLayout';
import { useWorkspace } from '@/stores/WorkspaceStore';
import { navGroupsFor, type NavGroup } from './navConfig';

/** Height of the bar itself, without the safe-area inset below it. */
export const BOTTOM_NAV_HEIGHT = 56;

/** How many daily items fit in the bar beside "More". */
const BAR_ITEMS = 4;

const MORE = '__more';

/** Whether a route covers where the browser is: the same path or a page under it. */
const covers = (to: string | undefined, pathname: string) => !!to && (pathname === to || pathname.startsWith(`${to}/`));

/**
 * FR-NAV-09: on a phone or a small tablet, the daily work sits in a bar at the foot of the screen — Home, the role's pinned
 * items, then the daily modules — with "More" opening the rest, Masters, Settings and the agency menu. The hamburger drawer
 * keeps working beside it.
 */
export default function BottomNav() {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hasAnyPermission } = useAuth();
  const { workspace } = useWorkspace();
  const rolePins = useRolePinGroups();
  const logout = useLogout();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const { daily, setup } = useMemo(() => {
    const groups = navGroupsFor(workspace).filter((g) => g.comingSoon || !g.perms?.length || hasAnyPermission(g.perms));
    // The bar holds direct links (and the not-yet-built modules, shown disabled); menus with children live in the drawer.
    const dailyGroups = groups.filter((g) => !g.section && (g.to || g.comingSoon));
    return {
      daily: withRolePins(dailyGroups, rolePins),
      setup: groups.filter((g) => g.section && g.to),
    };
  }, [workspace, hasAnyPermission, rolePins]);

  const bar = daily.slice(0, BAR_ITEMS);
  const rest = daily.slice(BAR_ITEMS);

  /** The most specific entry covering the current page — a pinned Hotels before the Masters hub that lists it. */
  const activeLabel = useMemo(() => {
    const candidates = [...daily, ...setup].filter((g) => !g.comingSoon && (covers(g.to, pathname)
      || (g.activeOn ?? []).some((p) => covers(p, pathname))));
    const exact = candidates.filter((g) => covers(g.to, pathname)).sort((a, b) => (b.to?.length ?? 0) - (a.to?.length ?? 0));
    return (exact[0] ?? candidates[0])?.label ?? null;
  }, [daily, setup, pathname]);

  if (!mobile) return null;

  const value = bar.some((g) => g.label === activeLabel) ? activeLabel : activeLabel ? MORE : false;
  const close = () => setMoreOpen(false);
  const go = (to: string) => { close(); navigate(to); };

  const handleLogout = async () => {
    close();
    await logout.mutateAsync();
    navigate('/login', { replace: true });
  };

  const drawerLink = (g: NavGroup) => (
    <ListItemButton
      key={g.label}
      disabled={g.comingSoon}
      selected={g.label === activeLabel}
      onClick={g.to ? () => go(g.to!) : undefined}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>{g.icon}</ListItemIcon>
      <ListItemText primary={g.label} primaryTypographyProps={{ fontSize: 14.5, fontWeight: 500 }} />
      {g.comingSoon && <Typography variant="caption" color="text.secondary">soon</Typography>}
    </ListItemButton>
  );

  const agencyItem = (label: string, icon: ReactNode, to: string) => (
    <ListItemButton key={label} onClick={() => go(to)}>
      <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
      <ListItemText primary={label} primaryTypographyProps={{ fontSize: 14.5, fontWeight: 500 }} />
    </ListItemButton>
  );

  return (
    <>
      <Paper
        component="nav"
        aria-label="daily navigation"
        elevation={0}
        sx={{
          position: 'fixed', left: 0, right: 0, bottom: 0,
          zIndex: (t) => t.zIndex.appBar,
          borderTop: 1, borderColor: 'divider',
          pb: 'env(safe-area-inset-bottom)',
          bgcolor: 'background.paper',
        }}
      >
        <BottomNavigation
          showLabels
          value={value}
          sx={{
            height: BOTTOM_NAV_HEIGHT,
            '& .MuiBottomNavigationAction-root': { minWidth: 0, px: 0.5 },
            '& .MuiBottomNavigationAction-label': {
              fontSize: 11, maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              '&.Mui-selected': { fontSize: 11, fontWeight: 700 },
            },
          }}
        >
          {bar.map((g) => (g.comingSoon || !g.to ? (
            <BottomNavigationAction key={g.label} value={g.label} label={g.label} icon={g.icon} disabled sx={{ opacity: 0.5 }} />
          ) : (
            <BottomNavigationAction key={g.label} value={g.label} label={g.label} icon={g.icon} component={RouterLink} to={g.to} />
          )))}
          <BottomNavigationAction value={MORE} label="More" icon={<MoreHorizRounded />} onClick={() => setMoreOpen(true)} />
        </BottomNavigation>
      </Paper>

      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={close}
        slotProps={{
          paper: {
            sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '80vh', pb: 'env(safe-area-inset-bottom)' },
          },
        }}
      >
        <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'divider', mx: 'auto', mt: 1, mb: 0.5 }} />
        <List dense={false} sx={{ py: 0.5 }}>
          {rest.length > 0 && <ListSubheader disableSticky>Daily work</ListSubheader>}
          {rest.map(drawerLink)}
          {setup.length > 0 && <ListSubheader disableSticky>Setup</ListSubheader>}
          {setup.map(drawerLink)}
          <Divider sx={{ my: 0.5 }} />
          <ListSubheader disableSticky>Agency</ListSubheader>
          {agencyItem('My Profile', <PersonOutlineOutlined />, '/profile')}
          {hasAnyPermission(['Company.View']) && agencyItem('Company profile', <BusinessOutlined />, '/business-profile/company')}
          {hasAnyPermission(['User.View', 'Role.View', 'Permission.View'])
            && agencyItem('Users and roles', <GroupOutlined />, hasAnyPermission(['User.View']) ? '/business-profile/staff' : '/business-profile/roles')}
          {hasAnyPermission(['Branch.View']) && agencyItem('Branches', <StoreMallDirectoryOutlined />, '/business-profile/branches')}
          <Divider sx={{ my: 0.5 }} />
          <ListItemButton onClick={handleLogout} sx={{ color: 'error.main' }}>
            <ListItemIcon sx={{ minWidth: 40 }}><LogoutOutlined color="error" /></ListItemIcon>
            <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: 14.5, fontWeight: 500 }} />
          </ListItemButton>
        </List>
      </Drawer>
    </>
  );
}
